import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TutorPage } from '../lib/tutorNotebook.ts';
import { mayCollapseNote } from '../lib/tutorNotebook.ts';
import { formatEquation, applyOperation } from '../../shared/algebra.ts';
import { WrittenBoard } from './WrittenBoard.tsx';
import { AnimatedBoard } from './AnimatedBoard.tsx';
import { OperationDemonstration } from './OperationDemonstration.tsx';
import { replyAlignment } from '../lib/replyAlignment.ts';
import { buildAlgebraTeachingPlan } from '../lib/algebraTeachingPlan.ts';
import { demonstrationProgress } from '../lib/workshopFlow.ts';
import type { NarratedWord } from '../lib/narratedOperation.ts';
import './tutor-notebook.css';
type Props = {
  page: TutorPage;
  pages: TutorPage[];
  style: 'written' | 'illustration';
  playing: boolean;
  busy: boolean;
  position: number;
  duration: number;
  words: readonly NarratedWord[];
  playbackId: number;
  reduced: boolean;
  onSelect: (id: string) => void;
  onPlay: () => void;
  onPause: () => void;
  onClose: () => void;
  onDelete: () => void;
  onAsk: (text: string) => void;
};
export function TutorNotebook(p: Props) {
  const [pinned, setPinned] = useState(false),
    [hovered, setHovered] = useState(false),
    [focused, setFocused] = useState(false),
    [visible, setVisible] = useState(!document.hidden),
    [remaining, setRemaining] = useState<number | null>(null),
    [question, setQuestion] = useState('');
  const wasPlaying = useRef(false);
  const canvasHeading = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!window.matchMedia('(max-width: 760px)').matches) return;
    const frame = requestAnimationFrame(() => {
      const heading = canvasHeading.current;
      if (!heading) return;
      const bounds = heading.getBoundingClientRect();
      // Reopening or starting playback reveals this notebook, without changing stage.
      if (bounds.top < 0 || bounds.top > window.innerHeight * .35)
        heading.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [p.page.id, p.playbackId, p.playing]);
  const [controlsSlot, setControlsSlot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => { setControlsSlot(document.getElementById('notebook-controls-slot')); });
  useLayoutEffect(() => {
    const notebook = canvasHeading.current?.closest<HTMLElement>('.tutor-notebook');
    const scene = notebook?.closest<HTMLElement>('.practice-scene');
    const paper = notebook?.querySelector<HTMLElement>('.written-paper-scroll');
    if (!notebook || !scene || !paper) return;
    const desktop = window.matchMedia('(min-width: 761px)');
    const measure = () => {
      if (!desktop.matches) {
        notebook.style.removeProperty('--notebook-paper-available');
        return;
      }
      const sceneBounds = scene.getBoundingClientRect();
      const bottom = Math.min(window.innerHeight, sceneBounds.top + scene.clientTop + scene.clientHeight);
      const available = Math.max(1, Math.floor(bottom - paper.getBoundingClientRect().top - 12));
      const value = `${available}px`;
      if (notebook.style.getPropertyValue('--notebook-paper-available') !== value)
        notebook.style.setProperty('--notebook-paper-available', value);
    };
    measure();
    const frame = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(scene);
    observer.observe(paper);
    observer.observe(canvasHeading.current!);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', measure);
      notebook.style.removeProperty('--notebook-paper-available');
    };
  }, [p.page.id, p.style, controlsSlot]);
  const interacting = hovered || focused;
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    setRemaining(null);
    setPinned(false);
    setQuestion('');
    wasPlaying.current = false;
  }, [p.page.id]);
  useEffect(() => {
    if (p.playing) setRemaining(null);
    else if (
      wasPlaying.current &&
      p.duration > 0 &&
      p.position >= p.duration - 100
    )
      setRemaining(8);
    wasPlaying.current = p.playing;
  }, [p.playing, p.duration, p.position]);
  useEffect(() => {
    if (
      remaining === null ||
      pinned ||
      interacting ||
      p.playing ||
      p.busy ||
      !visible
    )
      return;
    const timer = setTimeout(
      () => { if (!document.hidden) setRemaining((n) => (n === null ? null : n - 1)); },
      1000,
    );
    return () => clearTimeout(timer);
  }, [remaining, pinned, interacting, p.playing, p.busy, visible]);
  useEffect(() => {
    if (
      mayCollapseNote({
        remaining,
        pinned,
        interacting,
        playing: p.playing,
        busy: p.busy,
      })
    )
      p.onClose();
  }, [remaining, pinned, interacting, p.playing, p.busy]);
  const cue = replyAlignment({
    text: p.page.text,
    equation: p.page.equation,
    words: p.words,
    positionMs: p.position,
  });
  const plan = buildAlgebraTeachingPlan(
    p.page.equation,
    p.page.operation,
    p.words,
    p.duration,
    p.page.candidate,
  );
  const step = plan.steps.filter((s) => p.position >= s.startMs).at(-1);
  const amount = demonstrationProgress(
    p.words.length ? step : undefined,
    p.position,
    p.page.operation?.kind === 'divide',
    p.busy,
    p.reduced,
  );
  const equation =
    p.page.operation && amount >= 0.8
      ? applyOperation(p.page.equation, p.page.operation)
      : p.page.equation;
  const index = p.pages.findIndex((n) => n.id === p.page.id);
  const controls = <div className="notebook-sidebar-controls">
      <header className="notebook-header">
        <div>
          <span className="eyebrow">
            Tutor notebook · Page {index + 1} of {p.pages.length}
          </span>
          <h2>{p.page.title}</h2>
          <p>
            {formatEquation(p.page.equation)} · Saved explanation; your work
            stays unchanged.
          </p>
        </div>
        <button className="text-button" onClick={p.onClose}>
          Close notebook
        </button>
      </header>
      <nav className="notebook-controls" aria-label="Tutor notebook pages">
        <button
          className="secondary"
          disabled={index === 0}
          onClick={() => p.onSelect(p.pages[index - 1].id)}
        >
          Previous page
        </button>
        <label>
          Page
          <select
            value={p.page.id}
            onChange={(e) => p.onSelect(e.target.value)}
          >
            {p.pages.map((n, i) => (
              <option key={n.id} value={n.id}>
                {i + 1}. {n.title}
              </option>
            ))}
          </select>
        </label>
        <button
          className="secondary"
          disabled={index === p.pages.length - 1}
          onClick={() => p.onSelect(p.pages[index + 1].id)}
        >
          Next page
        </button>
      </nav>
      <div className="notebook-playback">
        <button
          className="primary"
          disabled={p.busy}
          onClick={p.playing ? p.onPause : p.onPlay}
        >
          {p.playing
            ? 'Pause this page'
            : p.busy
              ? 'Preparing this page…'
              : 'Play this page'}
        </button>
        <label>
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
          />{' '}
          Keep this page open
        </label>
        {remaining !== null && !pinned && (
          <span className="notebook-countdown" role="status">
            {interacting
              ? 'Reading pauses closing'
              : `Closes in ${Math.max(0, remaining)}s`}
            <progress max={8} value={Math.max(0, remaining)} />
          </span>
        )}
      </div>
      <form
        className="notebook-question"
        onSubmit={(e) => {
          e.preventDefault();
          if (question.trim()) p.onAsk(question);
        }}
      >
        <label htmlFor="note-question">Ask about this page</label>
        <div>
          <input
            id="note-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Why does this step work?"
            maxLength={1600}
          />
          <button className="secondary" disabled={!question.trim() || p.busy}>
            Ask tutor
          </button>
        </div>
      </form>
      <footer>
        <span>Saved on this browser. Reopen any page from Tutor notebook.</span>
        <button className="text-button" onClick={p.onDelete}>
          Delete this page
        </button>
      </footer>
  </div>;
  return (
    <section
      className={`tutor-notebook ${controlsSlot ? 'notebook-split' : ''}`}
      aria-label="Tutor notebook"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget) && !controlsSlot?.contains(e.relatedTarget)) setFocused(false);
      }}
      onWheel={() => setPinned(true)}
      onTouchMove={() => setPinned(true)}
    >
      {controlsSlot ? createPortal(controls, controlsSlot) : controls}
      <div className="notebook-canvas-heading" ref={canvasHeading}><span>Page {index + 1}</span><strong>{formatEquation(p.page.equation)}</strong><button className="secondary notebook-mobile-play" disabled={p.busy} onClick={p.playing?p.onPause:p.onPlay}>{p.playing?'Pause':p.busy?'Preparing…':'Play page'}</button></div>
      {p.style === 'written' || p.page.candidate !== undefined ? (
        <WrittenBoard
          equation={p.page.equation}
          operation={p.page.operation}
          candidate={p.page.candidate}
          noteText={p.page.text}
          words={p.words}
          positionMs={p.position}
          durationMs={p.duration}
          speaking={p.playing}
          reduced={p.reduced}
          playbackId={`${p.page.id}:${p.playbackId}`}
          label="Tutor’s saved explanation"
        />
      ) : (
        <>
          <AnimatedBoard
            equation={equation}
            frame={{
              kind: 'practice',
              phase: 0,
              progress: 1,
              focus: cue.focus,
              pointerFocus: cue.pointerFocus,
              word: cue.word,
              caption:
                cue.excerpt || 'Listen or read the saved explanation below.',
              equation: formatEquation(equation),
            }}
            speaking={p.playing}
            reduced={p.reduced}
            operation={p.page.operation}
            operationProgress={amount}
          />
          {p.page.operation && (
            <OperationDemonstration
              before={p.page.equation}
              operation={p.page.operation}
              after={applyOperation(p.page.equation, p.page.operation)}
              reduced={p.reduced}
              mediaProgress={amount}
            />
          )}
          <p className="notebook-prose">
            {cue.textRange ? (
              <>
                {p.page.text.slice(0, cue.textRange.start)}
                <mark>
                  {p.page.text.slice(cue.textRange.start, cue.textRange.end)}
                </mark>
                {p.page.text.slice(cue.textRange.end)}
              </>
            ) : (
              p.page.text
            )}
          </p>
        </>
      )}
    </section>
  );
}
